/** @module @liquidefy/code-editor/search @version 3.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  SearchState,
  SearchSession,
  SearchSessionId,
  SearchSessionStatus,
  SearchQuery,
  SearchMatch,
  SearchResult,
  SearchResultId,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asSearchResultId,
} from '../code-editor.js';

import { asSearchSessionId } from '../code-editor-types.js';

// ═══ §2 STATE FACTORY ═══

/**
 * Returns a fresh empty SearchState.
 */
export function createSearchState(): SearchState {
  return {
    sessions: [],
    activeSessionId: undefined,
  };
}

// ═══ §3 SESSION OPERATIONS ═══

/**
 * Begins a new search session — clears any existing session with the same id,
 * then appends a new running session.
 */
export function startSearch(
  state: SearchState,
  query: SearchQuery,
  sessionId: SearchSessionId,
  timestamp: number,
): SearchState {
  const filtered = state.sessions.filter((s) => s.id !== sessionId);
  const session: SearchSession = {
    id: sessionId,
    query,
    results: [],
    status: 'running',
    startedAt: timestamp,
    completedAt: undefined,
    totalMatchCount: 0,
  };
  return {
    sessions: [...filtered, session],
    activeSessionId: sessionId,
  };
}

/**
 * Appends a result to an existing session.
 */
export function addResult(
  state: SearchState,
  sessionId: SearchSessionId,
  result: SearchResult,
): SearchState {
  const sessions = state.sessions.map((s) => {
    if (s.id !== sessionId) return s;
    return {
      ...s,
      results: [...s.results, result],
      totalMatchCount: s.totalMatchCount + result.matches.length,
    };
  });
  return { ...state, sessions };
}

/**
 * Appends a match to an existing result within a session.
 */
export function addMatch(
  state: SearchState,
  sessionId: SearchSessionId,
  resultId: SearchResultId,
  match: SearchMatch,
): SearchState {
  const sessions = state.sessions.map((s) => {
    if (s.id !== sessionId) return s;
    const results = s.results.map((r) => {
      if (r.id !== resultId) return r;
      return { ...r, matches: [...r.matches, match] };
    });
    return {
      ...s,
      results,
      totalMatchCount: s.totalMatchCount + 1,
    };
  });
  return { ...state, sessions };
}

/**
 * Marks a session as complete.
 */
export function completeSearch(
  state: SearchState,
  sessionId: SearchSessionId,
  timestamp: number,
  status: SearchSessionStatus = 'complete',
): SearchState {
  const sessions = state.sessions.map((s) => {
    if (s.id !== sessionId) return s;
    return { ...s, status, completedAt: timestamp };
  });
  return { ...state, sessions };
}

/**
 * Clears all results for a session (resets it to idle with no results).
 */
export function clearSearch(
  state: SearchState,
  sessionId: SearchSessionId,
): SearchState {
  const sessions = state.sessions.filter((s) => s.id !== sessionId);
  const activeSessionId =
    state.activeSessionId === sessionId ? undefined : state.activeSessionId;
  return { sessions, activeSessionId };
}

/**
 * Wipes all sessions.
 */
export function clearAllSearches(state: SearchState): SearchState {
  return { sessions: [], activeSessionId: undefined };
}

// ═══ §4 QUERY ═══

/**
 * Returns all results for a session, or undefined if the session does not exist.
 */
export function getSession(
  state: SearchState,
  sessionId: SearchSessionId,
): readonly SearchResult[] | undefined {
  const session = state.sessions.find((s) => s.id === sessionId);
  return session?.results;
}

/**
 * Counts total matches across all results for a session.
 */
export function getTotalMatchCount(
  state: SearchState,
  sessionId: SearchSessionId,
): number {
  const session = state.sessions.find((s) => s.id === sessionId);
  return session ? session.totalMatchCount : 0;
}

/**
 * Filters results in a session by predicate.
 */
export function filterResults(
  state: SearchState,
  sessionId: SearchSessionId,
  predicate: (r: SearchResult) => boolean,
): SearchResult[] {
  const session = state.sessions.find((s) => s.id === sessionId);
  if (!session) return [];
  return session.results.filter(predicate);
}

// ═══ §5 REGEX & MATCHING ═══

/**
 * Constructs a RegExp from a SearchQuery.
 * Handles caseSensitive, wholeWord, and isRegex flags.
 * Returns null if the query pattern is empty or the regex is invalid.
 */
export function buildSearchRegex(query: SearchQuery): RegExp | null {
  const { pattern, isRegex, isCaseSensitive, isWholeWord } = query;
  if (!pattern) return null;

  let src = isRegex ? pattern : escapeRegex(pattern);
  if (isWholeWord) src = `\\b${src}\\b`;

  const flags = isCaseSensitive ? 'g' : 'gi';
  try {
    return new RegExp(src, flags);
  } catch {
    return null;
  }
}

/**
 * Runs the query against a text string.
 * Returns an array of SearchMatch objects with range and preview.
 */
export function matchesQuery(text: string, query: SearchQuery): SearchMatch[] {
  const re = buildSearchRegex(query);
  if (!re) return [];

  const lines = text.split('\n');
  const matches: SearchMatch[] = [];

  let lineStart = 0;
  for (let lineNumber = 0; lineNumber < lines.length; lineNumber++) {
    const line = lines[lineNumber]!;
    // Clone the regex for each line (reset lastIndex)
    const lineRe = new RegExp(re.source, re.flags.replace('g', '') + 'g');
    let m: RegExpExecArray | null;
    while ((m = lineRe.exec(line)) !== null) {
      const startCol = m.index;
      const endCol = m.index + m[0]!.length;
      matches.push({
        range: {
          start: { line: lineNumber, column: startCol },
          end: { line: lineNumber, column: endCol },
        },
        preview: line.trim(),
        lineNumber,
      });
    }
    lineStart += line.length + 1; // +1 for '\n'
  }

  return matches;
}

/**
 * Creates a new SearchSession id.
 */
export function createSearchSessionId(): SearchSessionId {
  return asSearchSessionId(generateId('search'));
}

/**
 * Creates a new SearchResult id.
 */
export function createSearchResultId(): SearchResultId {
  return asSearchResultId(generateId('sresult'));
}

// ═══ §6 INTERNAL HELPERS ═══

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
